import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Apiary,
  Colony,
  ColonyEvent,
  ColonyStatus,
  EventType,
  FeedType,
  HoneyType,
  Product,
  QueenCells,
  SuperType,
  TreatmentMethod,
  VarroaMethod,
} from '../../db/model';
import { getSetting, read, setSetting } from '../../db/db';
import { naturalLabel, fmt } from '../../db/queries';
import { ValidationError, saveEvent, setDeleted } from '../../db/actions';
import { checkWithdrawal, feedingSugarKg, infestationPercent, mitesPerDay, todayISO } from '../../db/rules';
import { back, go, useRoute } from '../hooks';
import { colonyName,
  COLONY_STATUS,
  EVENT_LABEL,
  FEED_TYPE,
  HONEY_TYPE,
  QUEEN_CELLS,
  SUPER_TYPE,
  TREATMENT_METHOD,
  VARROA_METHOD,
  fmtDate,
  plural,
} from '../labels';
import { Chips, DateInput, Field, Link, Notice, Rating, SaveBar, Screen, Section, Stepper, TextInput, YesNo, toast, errorText } from '../ui';
import { saved } from '../confirm';

// Formulář pracuje s volným objektem; typovou správnost hlídá validateEvent při uložení.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = Record<string, any> & { type: EventType; date: string; colonyIds: string[]; apiaryId: string };

/** Typy zápisů, u kterých se zadává teplota (výchozí 15 °C). */
const WITH_TEMPERATURE: EventType[] = ['treatment', 'inspection', 'varroa_check'];

const opts = <T extends string>(r: Record<T, string>) => Object.entries(r) as [T, string][];

interface Ctx {
  apiaries: Apiary[];
  colonies: Colony[];
  products: Product[];
  events: ColonyEvent[];
}

/**
 * Rozepsaný zápis, ze kterého člověk odešel bez uložení (Zpět, gesto, jiná
 * obrazovka). Oznámení „Vrátit“ ho otevře znovu i s vyplněnými údaji.
 */
let leftDraft: { hash: string; d: Draft } | null = null;

export function EventForm({ id }: { id?: string }) {
  const { query } = useRoute();
  const [ctx, setCtx] = useState<Ctx | null>(null);
  const [d, setD] = useState<Draft | null>(null);
  const [confirmWithdrawal, setConfirmWithdrawal] = useState(false);
  const formHash = useRef(location.hash);
  const initial = useRef<string | null>(null);
  const latest = useRef<Draft | null>(null);
  const done = useRef(false);
  latest.current = d;

  // odchod bez uložení: nic se neptá (gesto Zpět v telefonu nejde zastavit),
  // jen nabídne návrat k rozepsanému zápisu
  useEffect(
    () => () => {
      const cur = latest.current;
      if (done.current || !cur || initial.current === null || JSON.stringify(cur) === initial.current) return;
      leftDraft = { hash: formHash.current, d: cur };
      toast('Rozepsaný zápis nebyl uložen.', { label: 'Vrátit', run: () => go(formHash.current.replace(/^#\/?/, '')) });
    },
    [],
  );
  const start = (draft: Draft) => {
    initial.current = JSON.stringify(draft);
    const kept = leftDraft && leftDraft.hash === formHash.current ? leftDraft.d : null;
    leftDraft = null;
    setD(kept ?? draft);
  };

  useEffect(() => {
    (async () => {
      const [apiaries, colonies, products, events] = await Promise.all([
        read.all('apiaries'),
        read.all('colonies'),
        read.all('products'),
        read.all('events'),
      ]);
      setCtx({ apiaries, colonies, products: products.filter((p) => !p.hidden), events });
      if (id) {
        const e = events.find((x) => x.id === id);
        if (e) start({ ...e } as Draft);
        else setD(null);
        return;
      }
      const type = (query.get('typ') ?? 'note') as EventType;
      const colonyIds = (query.get('vcelstva') ?? '').split(',').filter(Boolean);
      const apiaryId = query.get('stanoviste') ?? colonies.find((c) => c.id === colonyIds[0])?.apiaryId ?? '';
      const draft: Draft = { type, date: todayISO(), colonyIds, apiaryId };
      const performedBy = (await getSetting<string>('performedBy')) ?? (await getSetting<{ name?: string }>('profile'))?.name;
      if (performedBy) draft.performedBy = performedBy;
      const single = colonyIds.length === 1 ? colonies.find((c) => c.id === colonyIds[0]) : undefined;
      if (WITH_TEMPERATURE.includes(type)) draft.temperatureC = 15;
      switch (type) {
        case 'inspection':
          // jen rozumný počet – pole je schované pod „Více údajů“, nesmyslná hodnota by zablokovala uložení
          if (single?.boxCount != null && single.boxCount >= 1 && single.boxCount <= 20) draft.boxCountAfter = single.boxCount;
          break;
        case 'treatment': {
          const lastProduct = await getSetting<string>('lastProductId');
          const p = products.find((x) => x.id === lastProduct && !x.hidden) ?? products.find((x) => !x.hidden);
          if (p) Object.assign(draft, await productDefaults(p));
          break;
        }
        case 'varroa_check':
          if (query.get('metoda') === 'winter_debris') Object.assign(draft, { method: 'winter_debris', sentToLab: true });
          else Object.assign(draft, { method: 'bottom_board', days: 7, sampleSize: 300 });
          break;
        case 'feeding':
          Object.assign(draft, { feedType: 'syrup_3_2', unit: 'kg', amountPerColony: await getSetting<number>('lastFeedAmount') });
          break;
        case 'harvest':
          draft.honeyType = 'flower';
          break;
        case 'move':
          draft.fromApiaryId = apiaryId;
          break;
        case 'status_change':
          draft.newStatus = 'dead';
          break;
        case 'super_change': {
          draft.action = query.get('akce') === 'remove' ? 'remove' : 'add';
          draft.count = 1;
          const onHive = single?.supers ?? [];
          draft.superType =
            draft.action === 'remove' && onHive.length
              ? onHive[onHive.length - 1].type
              : (await getSetting<string>('lastSuperType')) ?? 'low';
          break;
        }
      }
      start(draft);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const withdrawalConflicts = useMemo(
    () => (d?.type === 'harvest' && ctx ? checkWithdrawal(d as never, ctx.events.filter((e) => e.id !== d.id)) : []),
    [d, ctx],
  );

  if (!ctx) return null;
  if (!d) return <Screen title="Zápis nenalezen">{null}</Screen>;

  const set = (p: Partial<Draft>) => setD({ ...d, ...p });
  const apiary = ctx.apiaries.find((a) => a.id === d.apiaryId);
  const apiaryColonies = ctx.colonies
    .filter((c) => c.apiaryId === d.apiaryId && (c.status === 'active' || d.colonyIds.includes(c.id)))
    .sort(naturalLabel);
  const single = d.colonyIds.length === 1 ? ctx.colonies.find((c) => c.id === d.colonyIds[0]) : undefined;
  const title = EVENT_LABEL[d.type] + (!single && d.colonyIds.length > 1 ? ` – ${plural(d.colonyIds.length, 'včelstvo', 'včelstva', 'včelstev')}` : '');
  const bulkCapable = !['move', 'status_change', 'split', 'queen_change', 'swarm'].includes(d.type);
  // výběr dalších včelstev jen u hromadného zápisu (spuštěného ze stanoviště); zápis z karty včelstva je jen pro to včelstvo
  const bulkStart = (query.get('vcelstva') ?? '').split(',').filter(Boolean).length > 1;
  const structural = d.type === 'split' || d.type === 'queen_change';

  const save = async () => {
    if (withdrawalConflicts.length && !confirmWithdrawal) {
      setConfirmWithdrawal(true);
      return toast('Běží ochranná lhůta – zkontrolujte upozornění.');
    }
    try {
      // prázdný text = smazané pole (při úpravě se ze záznamu odstraní)
      const clean = Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v === '' ? undefined : v]));
      await saveEvent(clean as ColonyEvent);
      if (d.performedBy) await setSetting('performedBy', d.performedBy);
      if (d.type === 'treatment' && d.productId) {
        await setSetting('lastProductId', d.productId);
        if (d.batchNumber) await setSetting(`lastBatch:${d.productId}`, d.batchNumber);
        if (d.dosePerColony) await setSetting(`lastDose:${d.productId}`, d.dosePerColony);
      }
      if (d.type === 'feeding' && d.amountPerColony) await setSetting('lastFeedAmount', d.amountPerColony);
      if (d.type === 'super_change' && d.action === 'add') await setSetting('lastSuperType', d.superType);
      done.current = true;
      saved();
      back(single ? `vcelstvo/${single.id}` : `stanoviste/${d.apiaryId}`);
    } catch (e) {
      toast(e instanceof ValidationError ? e.errors.join(' ') : errorText(e));
    }
  };

  return (
    <Screen
      title={title}
      crumbs={[
        // včelstvo mimo stanoviště: drobeček na neexistující stanoviště by vedl na „nenalezeno“
        ...(apiary ? ([[apiary.name, `stanoviste/${d.apiaryId}`]] as [string, string][]) : []),
        ...(single ? ([[colonyName(single.label), `vcelstvo/${single.id}`]] as [string, string][]) : []),
      ]}
      backTo={single ? `vcelstvo/${single.id}` : `stanoviste/${d.apiaryId}`}
    >
      <div className="row two">
        <Field label="Datum" htmlFor="e-date">
          <DateInput id="e-date" value={d.date} max={todayISO()} onChange={(v) => set({ date: v })} />
        </Field>
        {WITH_TEMPERATURE.includes(d.type) && (
          <Field label="Teplota (°C)">
            <Stepper ariaLabel="Teplota" value={d.temperatureC} step={1} min={-20} max={45} unit="°C" onChange={(v) => set({ temperatureC: v })} />
          </Field>
        )}
      </div>

      {bulkCapable && bulkStart && !id && apiaryColonies.length > 1 && (
        <ColonyPicker colonies={apiaryColonies} selected={d.colonyIds} onChange={(colonyIds) => set({ colonyIds })} />
      )}

      {d.type === 'inspection' && <InspectionFields d={d} set={set} single={single} />}
      {d.type === 'treatment' && <TreatmentFields d={d} set={set} products={ctx.products} />}
      {d.type === 'varroa_check' && <VarroaFields d={d} set={set} />}
      {d.type === 'feeding' && <FeedingFields d={d} set={set} />}
      {d.type === 'harvest' && <HarvestFields d={d} set={set} colonies={ctx.colonies} />}
      {d.type === 'super_change' && <SuperFields d={d} set={set} single={single} editing={!!id} />}
      {d.type === 'move' && (
        <Field label="Přesunout na stanoviště" htmlFor="e-to">
          <select id="e-to" value={d.toApiaryId ?? ''} disabled={!!id} onChange={(e) => set({ toApiaryId: e.target.value })}>
            <option value="">– vyberte –</option>
            {ctx.apiaries
              .filter((a) => a.id !== d.fromApiaryId && !a.archived)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
      )}
      {d.type === 'status_change' && (
        <>
          <Field label="Nový stav">
            <Chips options={opts(COLONY_STATUS) as [ColonyStatus, string][]} value={d.newStatus} onChange={(v) => v && set({ newStatus: v })} />
          </Field>
          <Field label="Příčina / podrobnosti" htmlFor="e-cause">
            <TextInput id="e-cause" value={d.cause} onChange={(v) => set({ cause: v })} placeholder="např. hlad, varroáza, ztráta matky…" />
          </Field>
        </>
      )}
      {structural && <Notice>Údaje o matce / oddělku se upravují na kartě včelstva. Zde lze změnit datum a poznámku.</Notice>}

      {withdrawalConflicts.length > 0 && (
        <Notice tone="warn">
          <strong>Pozor, běží ochranná lhůta:</strong>
          <ul>
            {withdrawalConflicts.map((c) => (
              <li key={c.colonyId + c.treatmentId}>
                včelstvo {colonyName(ctx.colonies.find((x) => x.id === c.colonyId)?.label)}: {c.productName} do {fmtDate(c.until)}
              </li>
            ))}
          </ul>
          {confirmWithdrawal && 'Pokud jde o med z jiných nástavků, uložte znovu.'}
        </Notice>
      )}

      <Field label="Poznámka" htmlFor="e-note">
        <TextInput id="e-note" multiline value={d.note} onChange={(v) => set({ note: v })} />
      </Field>
      {(d.type === 'treatment' || d.type === 'inspection' || d.type === 'feeding') && (
        <Field label="Provedl(a)" htmlFor="e-by">
          <TextInput id="e-by" value={d.performedBy} onChange={(v) => set({ performedBy: v })} />
        </Field>
      )}

      {id && (
        <Section>
          <button
            className="btn danger"
            onClick={async () => {
              done.current = true;
              await setDeleted('events', id, true);
              saved('Zápis smazán', { label: 'Vrátit', run: () => setDeleted('events', id, false) });
              back(single ? `vcelstvo/${single.id}` : '');
            }}
          >
            Smazat zápis
          </button>
          {d.type === 'move' && <p className="muted small">Smazání přesunu nevrátí včelstvo na původní stanoviště.</p>}
        </Section>
      )}
      <SaveBar onSave={save} label={withdrawalConflicts.length && confirmWithdrawal ? 'Uložit i tak' : 'Uložit'} />
    </Screen>
  );
}

async function productDefaults(p: Product) {
  return {
    productId: p.id,
    productName: p.name,
    method: p.method ?? 'other',
    unit: p.unit,
    withdrawalDays: p.withdrawalDays,
    batchNumber: await getSetting<string>(`lastBatch:${p.id}`),
    dosePerColony: await getSetting<number>(`lastDose:${p.id}`),
  };
}

function ColonyPicker(props: { colonies: Colony[]; selected: string[]; onChange: (ids: string[]) => void }) {
  const all = props.colonies.length === props.selected.length;
  return (
    <Field
      label={`Včelstva (${props.selected.length})`}
      hint={
        <button className="btn link" type="button" onClick={() => props.onChange(all ? [] : props.colonies.map((c) => c.id))}>
          {all ? 'Zrušit výběr' : 'Vybrat všechna'}
        </button>
      }
    >
      <div className="chips">
        {props.colonies.map((c) => {
          const on = props.selected.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              className={'chip num' + (on ? ' on' : '')}
              aria-pressed={on}
              onClick={() => props.onChange(on ? props.selected.filter((x) => x !== c.id) : [...props.selected, c.id])}
            >
              {colonyName(c.label)}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

type FP = { d: Draft; set: (p: Partial<Draft>) => void };

const MORE_KEY = 'vcely-inspection-more';

function InspectionFields({ d, set, single }: FP & { single?: Colony }) {
  // podrobnosti jsou schované, dokud je uživatel nerozbalí (volba se pamatuje) nebo v nich nejsou vyplněné údaje
  const filled = d.broodPattern != null || d.broodFrames != null || d.storesKg != null || !!d.diseaseSuspicion;
  const [more, setMore] = useState(() => {
    try {
      return localStorage.getItem(MORE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const showMore = more || filled;
  const toggle = () => {
    const v = !showMore;
    setMore(v);
    try {
      localStorage.setItem(MORE_KEY, v ? '1' : '0');
    } catch {
      /* nevadí */
    }
  };
  return (
    <>
      <Section title="Matka">
        <YesNo label="Matka viděna" value={d.queenSeen} onChange={(v) => set({ queenSeen: v })} />
        <YesNo label="Vajíčka" value={d.eggsSeen} onChange={(v) => set({ eggsSeen: v })} />
        <Field label="Matečníky">
          <Chips options={opts(QUEEN_CELLS) as [QueenCells, string][]} value={d.queenCells} allowNone onChange={(v) => set({ queenCells: v })} />
        </Field>
      </Section>
      <Section title="Síla a povaha">
        <Field label="Obsazené uličky">
          <Stepper ariaLabel="Obsazené uličky" value={d.occupiedSeams} max={40} onChange={(v) => set({ occupiedSeams: v })} />
        </Field>
        <Field label="Mírnost (1–5)">
          <Rating ariaLabel="Mírnost" value={d.temperament} onChange={(v) => set({ temperament: v })} />
        </Field>
      </Section>
      <Field label="Provedené práce" htmlFor="e-act">
        <TextInput id="e-act" value={d.actions} onChange={(v) => set({ actions: v })} placeholder="přidán nástavek, 2 mezistěny…" />
      </Field>

      <button type="button" className="btn wide more-toggle" aria-expanded={showMore} onClick={toggle}>
        {showMore ? '▲ Méně údajů' : '▼ Více údajů (plod, zásoby, nástavky, nemoc)'}
      </button>
      {showMore && (
        <Section title="Podrobnosti">
          <Field label="Kvalita plodu (1–5)">
            <Rating ariaLabel="Kvalita plodu" value={d.broodPattern} onChange={(v) => set({ broodPattern: v })} />
          </Field>
          <div className="row two">
            <Field label="Plásty s plodem">
              <Stepper ariaLabel="Plásty s plodem" value={d.broodFrames} max={30} onChange={(v) => set({ broodFrames: v })} />
            </Field>
            <Field label="Zásoby (kg)">
              <Stepper ariaLabel="Zásoby" value={d.storesKg} step={1} max={60} unit="kg" onChange={(v) => set({ storesKg: v })} />
            </Field>
          </div>
          {single && (
            <Field label="Plodišťových nástavků po prohlídce">
              <Stepper ariaLabel="Plodišťových nástavků po prohlídce" value={d.boxCountAfter} min={1} max={8} onChange={(v) => set({ boxCountAfter: v })} />
            </Field>
          )}
          <YesNo label="Podezření na nemoc" value={d.diseaseSuspicion} onChange={(v) => set({ diseaseSuspicion: v })} />
          {d.diseaseSuspicion && (
            <Notice tone="warn">Při podezření na mor včelího plodu nebo jinou nebezpečnou nákazu kontaktujte KVS / nákazového referenta.</Notice>
          )}
        </Section>
      )}
    </>
  );
}

function TreatmentFields({ d, set, products }: FP & { products: Product[] }) {
  return (
    <>
      <Field label="Přípravek" htmlFor="e-prod" hint={<Link to="nastaveni">Upravit číselník přípravků</Link>}>
        <select
          id="e-prod"
          value={d.productId ?? ''}
          onChange={async (e) => {
            const p = products.find((x) => x.id === e.target.value);
            if (p) set(await productDefaults(p));
          }}
        >
          <option value="">– vyberte –</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Způsob aplikace">
        <Chips options={opts(TREATMENT_METHOD) as [TreatmentMethod, string][]} value={d.method} onChange={(v) => v && set({ method: v })} />
      </Field>
      <div className="row two">
        <Field label="Číslo šarže" htmlFor="e-batch">
          <TextInput id="e-batch" value={d.batchNumber} onChange={(v) => set({ batchNumber: v })} />
        </Field>
        <Field label={`Dávka na včelstvo${d.unit ? ` (${d.unit})` : ''}`}>
          <Stepper ariaLabel="Dávka" value={d.dosePerColony} step={1} unit={d.unit} onChange={(v) => set({ dosePerColony: v })} />
        </Field>
      </div>
      <div className="row two">
        <Field label="Ochranná lhůta (dní)" hint="dle příbalového letáku">
          <Stepper ariaLabel="Ochranná lhůta" value={d.withdrawalDays} max={365} unit="dní" onChange={(v) => set({ withdrawalDays: v })} />
        </Field>
        <Field label="Konec aplikace" htmlFor="e-end" hint="vyjmutí pásků apod.">
          <DateInput id="e-end" optional value={d.endDate} min={d.date} onChange={(v) => set({ endDate: v || undefined })} />
        </Field>
      </div>
      <div className="row two">
        <Field label="Pořadí v sérii" hint="např. 1. ze 3 fumigací">
          <Stepper ariaLabel="Pořadí" value={d.seriesIndex} min={1} max={10} onChange={(v) => set({ seriesIndex: v })} />
        </Field>
        <Field label="Celkem v sérii">
          <Stepper ariaLabel="Celkem" value={d.seriesTotal} min={1} max={10} onChange={(v) => set({ seriesTotal: v })} />
        </Field>
      </div>
    </>
  );
}

function VarroaFields({ d, set }: FP) {
  const preview = d.miteCount != null ? ({ ...d } as never) : null;
  const pd = preview ? mitesPerDay(preview) : null;
  const pct = preview ? infestationPercent(preview) : null;
  const isTest = d.method === 'alcohol_wash' || d.method === 'sugar_roll';
  const debris = d.method === 'winter_debris';
  return (
    <>
      <Field label="Metoda">
        <Chips
          options={opts(VARROA_METHOD) as [VarroaMethod, string][]}
          value={d.method}
          onChange={(v) => v && set(v === 'winter_debris' ? { method: v, sentToLab: true } : { method: v, sentToLab: d.method === 'winter_debris' ? undefined : d.sentToLab })}
        />
      </Field>
      {debris && (
        <Notice tone="info">
          Zapište den odběru měli. <strong>Výsledek vyšetření</strong> doplníte, až přijde z laboratoře – do té doby ho aplikace připomíná na úvodní
          obrazovce.
        </Notice>
      )}
      <div className="row two">
        <Field label={debris ? 'Výsledek – počet roztočů (doplníte později)' : 'Počet roztočů'}>
          <Stepper ariaLabel="Počet roztočů" value={d.miteCount} max={100000} onChange={(v) => set({ miteCount: v })} />
        </Field>
        {d.method === 'bottom_board' && (
          <Field label="Dní na podložce">
            <Stepper ariaLabel="Dní na podložce" value={d.days} min={1} max={60} onChange={(v) => set({ days: v })} />
          </Field>
        )}
        {isTest && (
          <Field label="Včel ve vzorku">
            <Stepper ariaLabel="Včel ve vzorku" value={d.sampleSize} step={50} max={2000} onChange={(v) => set({ sampleSize: v })} />
          </Field>
        )}
      </div>
      {pd != null && (
        <Notice tone={pd >= 10 ? 'warn' : 'ok'}>
          Přirozený spad <strong>{fmt(pd)} za den</strong>
          {pd >= 10 ? ' – vysoký, zvažte ošetření.' : '.'}
        </Notice>
      )}
      {pct != null && (
        <Notice tone={pct >= 3 ? 'warn' : 'ok'}>
          Napadení <strong>{fmt(pct)} %</strong>
          {pct >= 3 ? ' – nad orientační hranicí 3 %.' : '.'}
        </Notice>
      )}
      {!debris && (
        <label className="check">
          <input type="checkbox" checked={!!d.sentToLab} onChange={(e) => set({ sentToLab: e.target.checked || undefined })} />
          Zimní měl odeslána k vyšetření
        </label>
      )}
    </>
  );
}

function FeedingFields({ d, set }: FP) {
  const sugar = feedingSugarKg({ amountPerColony: d.amountPerColony ?? 0, unit: d.unit ?? 'kg', feedType: d.feedType ?? 'other' });
  return (
    <>
      <Field label="Krmivo">
        <Chips options={opts(FEED_TYPE) as [FeedType, string][]} value={d.feedType} onChange={(v) => v && set({ feedType: v })} />
      </Field>
      <Field label="Jednotka">
        <Chips
          ariaLabel="Jednotka"
          options={[
            ['l', 'litry (l)'],
            ['kg', 'kilogramy (kg)'],
          ]}
          value={d.unit}
          onChange={(v) => v && set({ unit: v })}
        />
      </Field>
      <Field label={`Množství na včelstvo (${d.unit === 'l' ? 'l' : 'kg'})`} hint="Tlačítka po 0,1 – nebo napište, např. 2,3">
        <Stepper ariaLabel="Množství" value={d.amountPerColony} step={0.1} max={50} onChange={(v) => set({ amountPerColony: v })} />
      </Field>
      {d.amountPerColony > 0 && (
        <p className="muted">
          ≈ {fmt(sugar)} kg cukru na včelstvo
          {d.colonyIds.length > 1 &&
            ` · celkem ${fmt(d.amountPerColony * d.colonyIds.length)} ${d.unit} (≈ ${fmt(sugar * d.colonyIds.length)} kg cukru)`}
        </p>
      )}
    </>
  );
}

function HarvestFields({ d, set, colonies }: FP & { colonies: Colony[] }) {
  const multi = d.colonyIds.length > 1;
  const per: Record<string, number> | undefined = d.perColonyKg;
  return (
    <>
      <Field label={multi ? 'Vytočeno celkem' : 'Vytočeno'}>
        <Stepper ariaLabel="Kg medu" value={d.honeyKg} step={0.5} max={2000} unit="kg" onChange={(v) => set({ honeyKg: v })} />
      </Field>
      <Field label="Druh medu">
        <Chips options={opts(HONEY_TYPE) as [HoneyType, string][]} value={d.honeyType} onChange={(v) => v && set({ honeyType: v })} />
      </Field>
      <div className="row two">
        <Field label="Vytočeno plástů">
          <Stepper ariaLabel="Plástů" value={d.framesExtracted} max={500} onChange={(v) => set({ framesExtracted: v })} />
        </Field>
        <Field label="Obsah vody (%)">
          <Stepper ariaLabel="Obsah vody" value={d.waterPercent} step={0.5} min={10} max={30} unit="%" onChange={(v) => set({ waterPercent: v })} />
        </Field>
      </div>
      <Field label="Šarže medu" htmlFor="e-hb" hint="pro označení sklenic při prodeji">
        <TextInput id="e-hb" value={d.honeyBatch} onChange={(v) => set({ honeyBatch: v })} placeholder={`např. ${d.date.slice(0, 4)}-01`} />
      </Field>
      {d.waterPercent > 18 && <Notice tone="warn">Med s obsahem vody nad 18 % hrozí kvašením (norma pro většinu medů je max. 20 %).</Notice>}
      {multi && (
        <Field label="Rozpis po včelstvech (nepovinné)">
          {per ? (
            <div className="per-colony">
              {d.colonyIds.map((cid) => (
                <div key={cid} className="row">
                  <span className="per-label">{colonyName(colonies.find((c) => c.id === cid)?.label)}</span>
                  <Stepper
                    ariaLabel={`Kg včelstvo ${colonyName(colonies.find((c) => c.id === cid)?.label)}`}
                    value={per[cid]}
                    unit="kg"
                    onChange={(v) => {
                      const next = { ...per, [cid]: v ?? 0 };
                      set({ perColonyKg: next, honeyKg: Object.values(next).reduce((a, b) => a + b, 0) });
                    }}
                  />
                </div>
              ))}
              <button className="btn link" type="button" onClick={() => set({ perColonyKg: undefined })}>
                Zrušit rozpis
              </button>
            </div>
          ) : (
            <button className="btn" type="button" onClick={() => set({ perColonyKg: Object.fromEntries(d.colonyIds.map((c) => [c, 0])) })}>
              Zadat po včelstvech
            </button>
          )}
        </Field>
      )}
    </>
  );
}

function SuperFields({ d, set, single, editing }: FP & { single?: Colony; editing: boolean }) {
  const onHive = single?.supers ?? [];
  return (
    <>
      <Field label="Co se dělá">
        <Chips
          ariaLabel="Akce"
          options={[
            ['add', 'Nasazení medníku'],
            ['remove', 'Odebrání medníku'],
          ]}
          value={d.action}
          onChange={(v) => v && set({ action: v })}
        />
      </Field>
      <Field label="Typ medníku">
        <Chips ariaLabel="Typ medníku" options={opts(SUPER_TYPE) as [SuperType, string][]} value={d.superType} onChange={(v) => v && set({ superType: v })} />
      </Field>
      <Field label="Počet">
        <Stepper ariaLabel="Počet medníků" value={d.count} min={1} max={6} onChange={(v) => set({ count: v })} />
      </Field>
      {single && !editing && (
        <p className="muted small">
          Teď na úlu: {onHive.length === 0 ? 'žádné medníky' : onHive.map((x) => (SUPER_TYPE[x.type] ?? SUPER_TYPE.other).toLowerCase()).join(', ')}
        </p>
      )}
      {editing && <Notice>Úprava zápisu nemění medníky na úlu. Seznam medníků opravíte v úpravě včelstva.</Notice>}
    </>
  );
}
